import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Car, DollarSign, BarChart3,
  User, LogOut, ChevronLeft, ChevronRight, Lock, CreditCard,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth.js'
import { useFeatures } from '../stores/subscriptionStore.js'
import CarFileLogo from './CarFileLogo.jsx'

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/vehicles', icon: Car, label: 'Vehicles' },
  { to: '/expenses', icon: DollarSign, label: 'Expenses', requiresFeature: 'expense_tracking' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', requiresFeature: 'basic_analytics' },
]

const bottomItems = [
  { to: '/subscription', icon: CreditCard, label: 'Subscription' },
  { to: '/profile', icon: User, label: 'Profile' },
]

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { logout, user } = useAuth()
  const features = useFeatures()

  const isLocked = (item) => {
    if (!item.requiresFeature) return false
    return !features[
      // Map feature flag → useFeatures convenience method
      item.requiresFeature === 'expense_tracking' ? 'canUseExpenses'
        : item.requiresFeature === 'basic_analytics' ? 'canUseAnalytics'
        : 'canUseExpenses'
    ]()
  }

  const renderNavItem = (item) => {
    const locked = isLocked(item)
    return (
      <NavLink
        key={item.to}
        to={locked ? '/subscription' : item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${
            isActive && !locked
              ? 'bg-brand-500/10 text-brand-500 dark:text-brand-400 font-medium'
              : locked
                ? 'text-gray-400 dark:text-gray-600 hover:text-gray-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
          }`
        }
      >
        <item.icon className="w-5 h-5 shrink-0" />
        {!collapsed && <span className="text-sm">{item.label}</span>}
        {locked && !collapsed && <Lock className="w-3.5 h-3.5 ml-auto text-amber-500" />}
        {locked && collapsed && (
          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
            <Lock className="w-2 h-2 text-white" />
          </div>
        )}
      </NavLink>
    )
  }

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-[72px]' : 'w-64'
      }`}
    >
      <div className="flex items-center justify-center px-5 h-16 border-b border-gray-200 dark:border-gray-800">
        <CarFileLogo size={collapsed ? 'sm' : 'md'} className="dark:hidden" />
        <CarFileLogo size={collapsed ? 'sm' : 'md'} className="hidden dark:block" dark />
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map(renderNavItem)}
      </nav>

      <div className="border-t border-gray-200 dark:border-gray-800 py-3 px-3 space-y-1">
        {bottomItems.map(renderNavItem)}
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-500/10 transition-all w-full"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
        {!collapsed && user && (
          <div className="text-xs text-gray-400 truncate px-3 pt-1">{user.email}</div>
        )}
        {!collapsed && (
          <div className="flex items-center gap-1.5 px-3 pt-2">
            <a
              href="https://apps.apple.com/us/app/car-file/id6757919957"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                alt="Download on the App Store"
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                className="h-7 hover:opacity-80 transition-opacity"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=app.carfile.v2&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                alt="Get it on Google Play"
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                className="h-[42px] hover:opacity-80 transition-opacity"
              />
            </a>
          </div>
        )}
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </button>
    </aside>
  )
}

export default Sidebar
